import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { tickerMetrics } from "../features/duel/tickerMetrics.ts";

interface TickerProps {
  text: string;
}

const ITEM_GAP_PX = 48;
const SPEED_PX_PER_SECOND = 52;

/**
 * Kayan yorum bandı — maçın spikeri.
 *
 * Track iki birebir aynı gruptan oluşur. Yarıya kaydığında ikinci grup
 * birincinin tam yerine gelir; grup içindeki boşluk da kopyalandığı için dikiş yoktur.
 */
export function Ticker({ text }: TickerProps) {
  const line = `${text} ///`;
  const viewportRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const groupRef = useRef<HTMLSpanElement>(null);
  const [metrics, setMetrics] = useState({ repeats: 2, duration: 9 });

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const probe = probeRef.current;
    const group = groupRef.current;
    if (!viewport || !probe || !group) return;

    const measure = () => {
      const viewportWidth = viewport.getBoundingClientRect().width;
      const itemWidth = probe.getBoundingClientRect().width;
      if (viewportWidth <= 0 || itemWidth <= 0) return;

      // Bir grubun kendisi görünür alanı aşar; iki grup birebir aynı kalır.
      const gap = Number.parseFloat(getComputedStyle(group).columnGap) || ITEM_GAP_PX;
      const { repeats } = tickerMetrics(viewportWidth, itemWidth, gap, SPEED_PX_PER_SECOND);
      const measuredGroupWidth = group.getBoundingClientRect().width;
      setMetrics((current) =>
        current.repeats === repeats &&
        Math.abs(current.duration - measuredGroupWidth / SPEED_PX_PER_SECOND) < 0.01
          ? current
          : {
              repeats,
              duration:
                current.repeats === repeats && measuredGroupWidth > 0
                  ? Math.max(6, measuredGroupWidth / SPEED_PX_PER_SECOND)
                  : current.duration,
            },
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(probe);
    observer.observe(group);
    return () => observer.disconnect();
  }, [line]);

  const style = { ["--ticker-duration"]: `${metrics.duration}s` } as CSSProperties;

  return (
    <div ref={viewportRef} className="ticker" aria-hidden="true">
      <span ref={probeRef} className="ticker__probe">
        {line}
      </span>
      <div key={`${text}-${metrics.repeats}`} className="ticker__track" style={style}>
        {[0, 1].map((group) => (
          <span
            ref={group === 0 ? groupRef : undefined}
            key={group}
            className="ticker__group"
          >
            {Array.from({ length: metrics.repeats }, (_, index) => (
              <span key={index} className="ticker__item">
                {line}
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
}
